import { TypedDocumentNode, gql } from '@apollo/client';

import { isType } from '@sorare/core/src/gql';

import { ArenaTicketOpeningOnboardingDialog } from './ArenaTicketOpeningOnboardingDialog';
import { CardOpeningOnboardingDialog } from './CardOpeningOnboardingDialog';
import { EssenceOpeningOnboardingDialog } from './EssenceOpeningOnboardingDialog';
import { BoxOpeningOnboardingDialog_probabilisticBundle } from './__generated__/index.graphql';
import { useBoxOpeningOnboardingDialog } from './useBoxOpeningOnboardingDialog';

type Props = {
  bundle: BoxOpeningOnboardingDialog_probabilisticBundle;
  open: boolean;
  disableOnClose?: boolean;
  onClose: () => void;
};

export const BoxOpeningOnboardingDialog = ({
  bundle,
  ...otherProps
}: Props) => {
  const slotItem = bundle.items.at(0);
  const dialogVariant = useBoxOpeningOnboardingDialog(bundle);

  if (dialogVariant === 'limited_card') {
    return <CardOpeningOnboardingDialog {...otherProps} bundle={bundle} />;
  }
  if (
    dialogVariant === 'arena_ticket' &&
    isType(slotItem, 'ProbabilisticBundleSlotArenaTicketItem')
  ) {
    return (
      <ArenaTicketOpeningOnboardingDialog
        {...otherProps}
        arenaTicketSlotItem={slotItem}
      />
    );
  }
  if (
    dialogVariant === 'limited_essence' &&
    isType(slotItem, 'ProbabilisticBundleSlotCardShardItem')
  ) {
    return (
      <EssenceOpeningOnboardingDialog
        {...otherProps}
        shardSlotItem={slotItem}
      />
    );
  }
  return null;
};

BoxOpeningOnboardingDialog.fragments = {
  probabilisticBundle: gql`
    fragment BoxOpeningOnboardingDialog_probabilisticBundle on ProbabilisticBundle {
      id
      items {
        id
        ... on ProbabilisticBundleSlotArenaTicketItem {
          id
          ...ArenaTicketOpeningOnboardingDialog_probabilisticBundleArenaTicketSlotItem
        }
        ... on ProbabilisticBundleSlotCardShardItem {
          id
          ...EssenceOpeningOnboardingDialog_probabilisticBundleSlotCardShardItem
        }
      }
      ...CardOpeningOnboardingDialog_probabilisticBundle
      ...useBoxOpeningOnboardingDialog_probabilisticBundle
    }
    ${CardOpeningOnboardingDialog.fragments.probabilisticBundle}
    ${ArenaTicketOpeningOnboardingDialog.fragments.arenaTicketSlotItem}
    ${EssenceOpeningOnboardingDialog.fragments.shardSlotItem}
    ${useBoxOpeningOnboardingDialog.fragments.probabilisticBundle}
  ` as TypedDocumentNode<BoxOpeningOnboardingDialog_probabilisticBundle>,
};
